import { Alert, DataTable, type ColumnDef, Card } from "@/deps/ui-library";
import { useFiscalYears, useProfitAndLoss } from "@/api/queries";
import type { ReportRow } from "@/api/types";
import {
  FrameworkStatementBody,
  StatementFormatSwitch,
  statementExport,
  useFrameworkStatement,
  useStatementFormat,
} from "./FrameworkStatement";
import { ReportFrame } from "./ReportFrame";
import { cn } from "@/lib/cn";
import { formatAmount } from "@/lib/money";

/**
 * Profit and loss (design lines 1318–1369).
 *
 * The untagged notice is not decoration. `reporting-layer-plan.md` §4 requires a completeness
 * check: every account with a balance is either in the statement or listed as unmapped, because
 * the legacy's report used an INNER JOIN against the layout and **silently omitted** any account
 * not attached to it (`04-reports.md` RULE 0.5).
 *
 * The statement is an RDS `DataTable`. Its section headings ("Income", "Expenses") are rows of the
 * statement, not column groups — a row with a label and no figures, so it cannot be read as a zero —
 * and the weight of a subtotal or the total is the table's `rowClassName`, one decision per row.
 */
export function ProfitAndLossPage() {
  // This year against the one before it, whichever order the years arrive in.
  const { data: years } = useFiscalYears();
  const newestFirst = [...(years ?? [])].sort((x, y) => y.start.localeCompare(x.start));
  const current = newestFirst.find((year) => year.isCurrent) ?? newestFirst[0];
  const prior = newestFirst.find((year) => current !== undefined && year.end < current.start);
  const period = current?.label ?? "";
  const compare = prior?.label;
  const { data, isPending } = useProfitAndLoss({ period, compare });
  const chosen = useStatementFormat();
  const framework = useFrameworkStatement(chosen.format, "pl", {
    from: current?.start ?? null,
    to: current?.end ?? "",
    compareFrom: prior?.start ?? null,
    compareTo: prior?.end ?? null,
  });

  return (
    <ReportFrame
      id="pl"
      title="Profit and loss"
      period={data?.period}
      exportable={
        framework.on
          ? framework.rendered.data
            ? () => statementExport(framework.rendered.data!, period)
            : undefined
          : data
            ? () => ({
                title: "Profit and loss",
                period: data.period,
                columns: [
                  { header: "Account" },
                  { header: data.period, numeric: true },
                  ...(data.compare ? [{ header: data.compare, numeric: true }] : []),
                ],
                rows: data.rows.map((row) => [
                  row.label,
                  row.current,
                  ...(data.compare ? [row.compare] : []),
                ]),
              })
            : undefined
      }
    >
      <StatementFormatSwitch {...chosen} />

      {framework.on ? <FrameworkStatementBody framework={framework} /> : null}

      {!framework.on && data && data.untaggedCount > 0 ? (
        <Alert
          role="status"
          config={{ variant: "subtle", colorScheme: "warning" }}
          description={`${data.untaggedCount} account${data.untaggedCount === 1 ? "" : "s"} with a balance ${
            data.untaggedCount === 1 ? "is" : "are"
          } not mapped to a report line, so ${
            data.untaggedCount === 1 ? "it is" : "they are"
          } not in the figures below. The legacy report left them out without saying so.`}
        />
      ) : null}

      <Card size="none" className={framework.on ? "hidden" : "overflow-hidden"}>
        <DataTable
          aria-label={data?.title ?? "Profit and loss"}
          size="sm"
          enableSorting={false}
          columns={statementColumns(data?.period ?? "", data?.compare ?? null)}
          data={data?.rows ?? []}
          getRowId={(row, index) => `${row.label}-${index}`}
          rowClassName={({ original: row }) => rowClass(row)}
          loading={isPending}
          states={{
            loading: (
              <div className="flex flex-col gap-2 py-2" aria-busy="true">
                {Array.from({ length: 10 }, (_, i) => (
                  // oxlint-disable-next-line react/no-array-index-key -- placeholders, never reordered
                  <span key={i} className="bg-muted h-4 animate-pulse rounded" aria-hidden="true" />
                ))}
              </div>
            ),
          }}
          emptyContent="Nothing to report for this period."
          pagination={{ mode: "manual", show: false }}
          containerClassName="rounded-none border-0"
        />
      </Card>
    </ReportFrame>
  );
}

/** A subtotal is medium weight, the total semibold on a well — a fact about the row, so the table
 * carries it on the row rather than each column repeating it. */
function rowClass(row: ReportRow) {
  return cn(
    row.kind === "total" && "bg-muted font-semibold",
    row.kind === "subtotal" && "font-medium",
  );
}

const amount = (value: string | null) => (value === null ? "" : formatAmount(value));

/**
 * The statement's columns. Built outside the component: a column's `cell` is a render function,
 * and defining them in the body would make each render a new column model.
 */
function statementColumns(period: string, compare: string | null): Array<ColumnDef<ReportRow>> {
  const columns: Array<ColumnDef<ReportRow>> = [
    {
      id: "account",
      header: "Account",
      meta: {
        cellClassName: ({ original: row }) =>
          row.kind === "header" ? "text-muted-foreground font-medium" : "text-foreground",
      },
      cell: ({ row: { original: row } }) => (
        <span className="block" style={{ paddingLeft: `${row.depth * 16}px` }}>
          {row.label}
        </span>
      ),
    },
    {
      id: "current",
      header: period,
      meta: {
        textAlign: "right",
        numeric: true,
        cellClassName: "text-foreground",
      },
      cell: ({ row: { original: row } }) => amount(row.current),
    },
  ];
  if (compare) {
    columns.push(
      {
        id: "compare",
        header: compare,
        meta: {
          textAlign: "right",
          numeric: true,
          cellClassName: "text-muted-foreground",
        },
        cell: ({ row: { original: row } }) => amount(row.compare),
      },
      {
        id: "change",
        header: "Change",
        meta: {
          textAlign: "right",
          cellClassName: ({ original: row }) =>
            cn(
              "num",
              row.deltaPct === null
                ? "text-muted-foreground"
                : row.deltaPct >= 0
                  ? "text-ok-text"
                  : "text-bad-text",
            ),
        },
        cell: ({ row: { original: row } }) =>
          row.deltaPct === null ? "" : `${row.deltaPct > 0 ? "+" : ""}${row.deltaPct}%`,
      },
    );
  }
  return columns;
}
